import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FolderOpen, Plus, Search, Eye, Edit, Edit3, Trash2,
  Building, Calendar, Package, CheckCircle, XCircle,
  Clock, Upload, X, Info, AlertTriangle, MapPin, Files, ArrowLeft, Tag,
  UserPlus, Shield, UserCheck, Sparkles, Mail, Send, FileText, Users,
  Check, Image, MoreVertical
} from 'lucide-react';
import toast from 'react-hot-toast';
import dataroomService from '../services/dataroomService';
import pricingPlanService from '../services/pricingPlanService';
import { useDebounce } from '../hooks/useDebounce';
import { useAuth } from '../contexts/AuthContext';
import CommonLoader, { GridLoader, PageLoader } from '../components/CommonLoader';
import { CreateButton, DeleteButton, UpdateButton, CancelButton, SaveButton } from '../components/CommonButtons';
import EditDataroomForm from '../components/EditDataroomForm';
import { validateRequired, validateEmail, validatePhone, validatePAN, validateGSTIN, validateTAN, ErrorMessage } from '../utils/validation';

const DataroomList = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [datarooms, setDatarooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedDataroom, setSelectedDataroom] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [pricingPlans, setPricingPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [wizardStep, setWizardStep] = useState(1); // 1: Select Plan, 2: Form Details
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showMenuDropdown, setShowMenuDropdown] = useState(null); // Track which dataroom's menu is open
  const [formData, setFormData] = useState({
    name: '',
    logo: null,
    company_name: '',
    brand_name: '',
    organization_type: '',
    registered_office_address: '',
    industry: '',
    transaction_type: '',
    corporate_identification_no: '',
    pan_number: '',
    tan_number: '',
    gstin: '',
    website: '',
    primary_contact_name: '',
    primary_contact_no: '',
    primary_contact_email: '',
    secondary_contact_name: '',
    secondary_contact_no: '',
    secondary_contact_email: '',
    organization_email: '',
    pricing_plan_id: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [logoPreview, setLogoPreview] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  // Debounce search term
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  useEffect(() => {
    fetchDatarooms();
    // Fetch pricing plans for both admin and super-admin users
    fetchPricingPlans();
  }, [debouncedSearchTerm, selectedStatus]);

  // Close menu dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showMenuDropdown && !event.target.closest('.dataroom-menu-dropdown')) {
        setShowMenuDropdown(null);
      }
    };

    if (showMenuDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showMenuDropdown]);

  const fetchDatarooms = async () => {
    try {
      setLoading(true);
      const params = {
        search: debouncedSearchTerm,
        status: selectedStatus
      };

      // Use My Datarooms endpoint for admin users
      const response = await dataroomService.getMyDatarooms(params);
      
      // Use normalized response structure
      setDatarooms(response.data || []);
    } catch (error) {
      toast.error('Failed to fetch datarooms');
    } finally {
      setLoading(false);
    }
  };

  const fetchPricingPlans = async () => {
    try {
      setPlansLoading(true);
      const response = await pricingPlanService.getAvailablePricingPlans();
      
      // Use normalized response structure
      setPricingPlans(response.data || []);
    } catch (error) {
      toast.error('Failed to fetch pricing plans');
    } finally {
      setPlansLoading(false);
    }
  };


  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) errors.name = 'Name is required';
    if (!formData.company_name.trim()) errors.company_name = 'Company name is required';
    if (!formData.organization_email.trim()) {
      errors.organization_email = 'Organization email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.organization_email)) {
      errors.organization_email = 'Invalid email address';
    }

    // Validate newly required fields
    if (!formData.organization_type.trim()) errors.organization_type = 'Organization type is required';
    if (!formData.industry.trim()) errors.industry = 'Industry is required';
    if (!formData.registered_office_address.trim()) errors.registered_office_address = 'Registered office address is required';
    if (!formData.corporate_identification_no.trim()) errors.corporate_identification_no = 'Corporate identification no is required';
    if (!formData.pan_number.trim()) errors.pan_number = 'PAN number is required';
    if (!formData.tan_number.trim()) errors.tan_number = 'TAN number is required';
    if (!formData.gstin.trim()) errors.gstin = 'GSTIN is required';

    // Validate website
    if (!formData.website.trim()) {
      errors.website = 'Website is required';
    } else if (!/^https?:\/\/.+\..+/.test(formData.website)) {
      errors.website = 'Please enter a valid URL (e.g., https://example.com)';
    }

    // Validate primary contact person
    if (!formData.primary_contact_name.trim()) errors.primary_contact_name = 'Primary contact person name is required';
    if (!formData.primary_contact_no.trim()) {
      errors.primary_contact_no = 'Primary contact number is required';
    } else if (!/^[+]?[\d\s-]{10,}$/.test(formData.primary_contact_no)) {
      errors.primary_contact_no = 'Please enter a valid phone number';
    }
    if (!formData.primary_contact_email.trim()) {
      errors.primary_contact_email = 'Primary contact email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.primary_contact_email)) {
      errors.primary_contact_email = 'Invalid email address';
    }

    // Validate secondary contact person
    if (!formData.secondary_contact_name.trim()) errors.secondary_contact_name = 'Secondary contact person name is required';
    if (!formData.secondary_contact_no.trim()) {
      errors.secondary_contact_no = 'Secondary contact number is required';
    } else if (!/^[+]?[\d\s-]{10,}$/.test(formData.secondary_contact_no)) {
      errors.secondary_contact_no = 'Please enter a valid phone number';
    }
    if (!formData.secondary_contact_email.trim()) {
      errors.secondary_contact_email = 'Secondary contact email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.secondary_contact_email)) {
      errors.secondary_contact_email = 'Invalid email address';
    }

    // Validate pricing plan for all users
    if (!formData.pricing_plan_id) {
      errors.pricing_plan_id = 'Please select a pricing plan';
    }

    console.log(errors);
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handlePlanSelection = (plan) => {
    setSelectedPlan(plan);
    setFormData({ ...formData, pricing_plan_id: plan.id.toString() });
  };

  const handleNextStep = () => {
    if (!selectedPlan) {
      toast.error('Please select a pricing plan');
      return;
    }
    setWizardStep(2);
  };

  const handlePreviousStep = () => {
    setWizardStep(1);
  };

  const handleCreateDataroom = async (e) => {
    e.preventDefault();

    console.log(validateForm());
    if (!validateForm()) return;

    try {
      // Convert formData object to FormData for multipart/form-data submission
      const submitData = new FormData();

      // Append all form fields to FormData
      Object.keys(formData).forEach(key => {
        const value = formData[key];

        // Skip null, undefined, and empty strings (except for logo which can be null)
        if (value === null || value === undefined) {
          return;
        }

        // Skip empty strings but allow 0 and false
        if (value === '' && key !== 'logo') {
          return;
        }

        // Convert email fields to lowercase
        if (key === 'organization_email' || key === 'primary_contact_email' || key === 'secondary_contact_email') {
          submitData.append(key, value.toLowerCase());
        } else {
          submitData.append(key, value);
        }
      });

      // Debug: Log what's being sent
      console.log('=== Create Dataroom Debug ===');
      console.log('Original formData:', formData);
      console.log('FormData entries:');
      for (let pair of submitData.entries()) {
        console.log(pair[0] + ':', pair[1]);
      }

      await dataroomService.createDataroom(submitData);
      toast.success('Dataroom created successfully! Awaiting approval.');
      setShowCreateModal(false);
      resetForm();
      fetchDatarooms();
    } catch (error) {
      console.error('Create dataroom error:', error);
      toast.error(error.response?.data?.message || 'Failed to create dataroom');
    }
  };

  const handleDeleteDataroom = async () => {
    try {
      await dataroomService.deleteDataroom(selectedDataroom.uuid);
      toast.success('Dataroom deleted successfully');
      setShowDeleteModal(false);
      setSelectedDataroom(null);
      fetchDatarooms();
    } catch (error) {
      toast.error('Failed to delete dataroom');
    }
  };

  const handleViewDetails = async (dataroom) => {
    setSelectedDataroom(dataroom);
    setShowDetailsModal(true);

    try {
      const response = await dataroomService.getDataroomDetails(dataroom.uuid);
      setSelectedDataroom(response.data || response);
    } catch (error) {
      toast.error('Failed to fetch dataroom details');
    }
  };

  const handleEditDataroom = async (dataroom) => {
    setSelectedDataroom(dataroom);
    setShowEditModal(true);

    try {
      const response = await dataroomService.getDataroomDetails(dataroom.uuid);
      setSelectedDataroom(response.data || response);
    } catch (error) {
      toast.error('Failed to fetch dataroom details');
    }
  };

  const handleUpdateDataroom = async (updatedData) => {
    const toastId = toast.loading('Updating dataroom...');
    try {
      // Create FormData for the API call
      const formData = new FormData();
      Object.keys(updatedData).forEach(key => {
        if (updatedData[key] !== null && updatedData[key] !== undefined && updatedData[key] !== '') {
          // Convert email fields to lowercase
          if (key === 'organization_email' || key === 'primary_contact_email' || key === 'secondary_contact_email') {
            formData.append(key, updatedData[key].toLowerCase());
          } else {
            formData.append(key, updatedData[key]);
          }
        }
      });

      const response = await dataroomService.updateDataroom(selectedDataroom.uuid, formData);
      toast.success('Dataroom updated successfully', { id: toastId });

      // Refresh the datarooms list
      fetchDatarooms();
      setShowEditModal(false);
      return response;
    } catch (error) {
      console.error('Failed to update dataroom:', error);
      toast.error(error.response?.data?.message || 'Failed to update dataroom', { id: toastId });
      throw error;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch (error) {
      return dateString;
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    processFile(file);
  };

  const processFile = (file) => {
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/jpg'];
      if (!validTypes.includes(file.type)) {
        toast.error('Please upload a valid image file (JPG, PNG, or GIF)');
        return;
      }

      // Validate file size (2MB)
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Logo size should be less than 2MB');
        return;
      }
      
      setFormData({ ...formData, logo: file });
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target.result);
      };
      reader.readAsDataURL(file);
      
      // Show success message
      toast.success(`Logo "${file.name}" uploaded successfully`);
    }
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      processFile(files[0]);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      logo: null,
      company_name: '',
      brand_name: '',
      organization_type: '',
      registered_office_address: '',
      industry: '',
      transaction_type: '',
      corporate_identification_no: '',
      pan_number: '',
      tan_number: '',
      gstin: '',
      secondary_contact_name: '',
      secondary_contact_no: '',
      organization_email: '',
      pricing_plan_id: ''
    });
    setFormErrors({});
    setLogoPreview(null);
    setWizardStep(1);
    setSelectedPlan(null);
  };

  const getStatusBadge = (status, statusText) => {
    // Use text value for display
    const displayText = statusText || status;
    
    // Determine config based on numeric or text status
    let configKey = 'inactive';
    if (status === 1 || status === 'active' || statusText?.toLowerCase() === 'active') {
      configKey = 'active';
    } else if (status === 2 || status === 'suspended' || statusText?.toLowerCase() === 'suspended') {
      configKey = 'suspended';
    } else if (status === 0 || status === 'inactive' || statusText?.toLowerCase() === 'inactive') {
      configKey = 'inactive';
    }
    
    const statusConfig = {
      active: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      inactive: { color: 'bg-gray-100 text-gray-800', icon: XCircle },
      suspended: { color: 'bg-red-100 text-red-800', icon: AlertTriangle }
    };

    const config = statusConfig[configKey];
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {displayText}
      </span>
    );
  };

  const getApprovalBadge = (approvalStatus, approvalText) => {
    // Use text value for display
    const displayText = approvalText || approvalStatus;
    
    // Determine config based on numeric or text status
    let configKey = 'pending';
    if (approvalStatus === 1 || approvalStatus === 'approved' || approvalText?.toLowerCase() === 'approved') {
      configKey = 'approved';
    } else if (approvalStatus === 0 || approvalStatus === 'pending' || approvalText?.toLowerCase() === 'pending') {
      configKey = 'pending';
    } else if (approvalStatus === 2 || approvalStatus === 'rejected' || approvalText?.toLowerCase() === 'rejected') {
      configKey = 'rejected';
    }
    
    const statusConfig = {
      approved: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      rejected: { color: 'bg-red-100 text-red-800', icon: XCircle }
    };

    const config = statusConfig[configKey];
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {displayText}
      </span>
    );
  };

  return (
    <div>
      <div className="mb-4 sm:mb-6">
        <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">My Datarooms</h1>
        <p className="mt-1 text-xs sm:text-sm text-gray-600">
          Manage your datarooms and track their approval status
        </p>
      </div>

      {/* Header Actions */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between mb-6">
          <div className="flex flex-col sm:flex-row gap-4 flex-1">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search datarooms..."
                className="w-full pl-10 pr-4 py-1.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>

            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm bg-white hover:border-gray-400 transition-colors appearance-none cursor-pointer shadow-sm"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236B7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                backgroundPosition: 'right 0.5rem center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: '1.25rem'
              }}
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>

          <CreateButton
            onClick={() => setShowCreateModal(true)}
            icon={Plus}
            size="sm"
          >
            <span className="hidden sm:inline">Create Dataroom</span>
            <span className="sm:hidden">Create</span>
          </CreateButton>
      </div>

      {/* Datarooms Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5">
        {loading ? (
          [...Array(9)].map((_, index) => (
            <div key={index} className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-6">
              {/* Header skeleton */}
              <div className="flex items-center mb-4">
                <div className="bg-gray-200 h-12 w-12 rounded-lg mr-4 animate-pulse"></div>
                <div className="flex-1">
                  <div className="bg-gray-200 h-6 w-40 rounded animate-pulse mb-2"></div>
                  <div className="bg-gray-200 h-4 w-32 rounded animate-pulse"></div>
                </div>
              </div>
              
              {/* Info skeleton */}
              <div className="space-y-3 mb-4">
                <div className="flex items-center">
                  <div className="bg-gray-200 h-4 w-4 rounded mr-2 animate-pulse"></div>
                  <div className="bg-gray-200 h-4 w-28 rounded animate-pulse"></div>
                </div>
                <div className="flex items-center">
                  <div className="bg-gray-200 h-4 w-4 rounded mr-2 animate-pulse"></div>
                  <div className="bg-gray-200 h-4 w-36 rounded animate-pulse"></div>
                </div>
                <div className="flex items-center">
                  <div className="bg-gray-200 h-4 w-4 rounded mr-2 animate-pulse"></div>
                  <div className="bg-gray-200 h-4 w-24 rounded animate-pulse"></div>
                </div>
              </div>
              
              {/* Status badges skeleton */}
              <div className="flex items-center justify-between mb-4">
                <div className="bg-gray-200 h-6 w-16 rounded-full animate-pulse"></div>
                <div className="bg-gray-200 h-6 w-20 rounded-full animate-pulse"></div>
              </div>
              
              {/* Action buttons skeleton */}
              <div className="pt-4 border-t border-gray-200">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="bg-gray-200 h-8 w-full rounded animate-pulse"></div>
                  ))}
                </div>
              </div>
            </div>
          ))
        ) : datarooms.length === 0 ? (
          <div className="col-span-full text-center py-12">
            <FolderOpen className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No datarooms</h3>
            <p className="mt-1 text-sm text-gray-500">Get started by creating a new dataroom.</p>
            <div className="mt-6">
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 shadow-sm hover:shadow transition-all"
              >
                <Plus className="w-5 h-5 mr-2" />
                Create Dataroom
              </button>
            </div>
          </div>
        ) : (
          datarooms.map((dataroom) => (
            <motion.div
              key={dataroom.id || dataroom.uuid}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-lg shadow-sm border-2 border-gray-200 hover:shadow-md transition-all cursor-pointer"
              onClick={() => {
                // Navigate to manage files only if dataroom is approved and active
                if ((dataroom.approval_status === 1 || dataroom.approval_status === 'approved' || dataroom.approval_status_text?.toLowerCase() === 'approved') &&
                    (dataroom.status === 1 || dataroom.status === 'active' || dataroom.status_text?.toLowerCase() === 'active')) {
                  navigate(`/datarooms/${dataroom.uuid}/files`);
                }
              }}
            >
              <div className="p-4 sm:p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center flex-1">
                    {(dataroom.logo_url || dataroom.logo) ? (
                      <img
                        className="h-10 w-10 sm:h-12 sm:w-12 rounded-lg object-cover flex-shrink-0"
                        src={dataroom.logo_url || dataroom.logo}
                        alt={dataroom.name}
                      />
                    ) : (
                      <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-lg bg-gray-300 flex items-center justify-center flex-shrink-0">
                        <FolderOpen className="h-5 w-5 sm:h-6 sm:w-6 text-gray-600" />
                      </div>
                    )}
                    <div className="ml-2 sm:ml-3 min-w-0 flex-1">
                      <h3 className="text-base sm:text-lg font-medium text-gray-900 truncate">{dataroom.name}</h3>
                      <p className="text-xs sm:text-sm text-gray-500 truncate">{dataroom.company_name}</p>
                    </div>
                  </div>

                  {/* 3-dots menu for dataroom options - positioned on right */}
                  {/* Hide menu when approval_status is 0 (pending) */}
                  {dataroom.approval_status !== 0 && (
                    <div className="relative dataroom-menu-dropdown" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowMenuDropdown(showMenuDropdown === dataroom.uuid ? null : dataroom.uuid);
                        }}
                        className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                        title="More options"
                      >
                        <MoreVertical className="w-4 h-4 text-gray-500" />
                      </button>

                    {showMenuDropdown === dataroom.uuid && (
                      <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                        {/* Tags - Always visible */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/datarooms/${dataroom.uuid}/tags`);
                            setShowMenuDropdown(null);
                          }}
                          className={`w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 ${
                            dataroom.is_owner !== true ? 'rounded-lg' : 'rounded-t-lg'
                          }`}
                        >
                          <Tag className="w-4 h-4" />
                          Tags
                        </button>

                        {/* Show other options only for owners */}
                        {dataroom.is_owner === true && (
                          <>
                            {/* Invitations */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/datarooms/${dataroom.uuid}/invitations`);
                                setShowMenuDropdown(null);
                              }}
                              className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                            >
                              <UserPlus className="w-4 h-4" />
                              Invitations
                            </button>

                            {/* Roles */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/datarooms/${dataroom.uuid}/roles`);
                                setShowMenuDropdown(null);
                              }}
                              className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                            >
                              <Shield className="w-4 h-4" />
                              Roles
                            </button>

                            {/* Members */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/datarooms/${dataroom.uuid}/members`);
                                setShowMenuDropdown(null);
                              }}
                              className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 rounded-b-lg"
                            >
                              <Users className="w-4 h-4" />
                              Members
                            </button>
                          </>
                        )}
                      </div>
                    )}
                    </div>
                  )}
                </div>

                <div className="space-y-1.5 sm:space-y-2 mb-3">
                  {/* Show pricing plan for all users */}
                  <div className="flex items-center text-xs sm:text-sm text-gray-600">
                    <Package className="w-3.5 sm:w-4 h-3.5 sm:h-4 mr-1.5 sm:mr-2 flex-shrink-0" />
                    <span className="truncate">{dataroom.pricing_plan?.name || 'No plan'}</span>
                  </div>
                  <div className="flex items-center text-xs sm:text-sm text-gray-600">
                    <Calendar className="w-3.5 sm:w-4 h-3.5 sm:h-4 mr-1.5 sm:mr-2 flex-shrink-0" />
                    <span className="truncate">Created: {formatDate(dataroom.created_at)}</span>
                  </div>
                  {/* Subscription Period */}
                  {dataroom.subscription && dataroom.subscription.current_period_starts_at && dataroom.subscription.current_period_ends_at && (
                    <div className="flex items-center text-xs sm:text-sm text-gray-600">
                      <Clock className="w-3.5 sm:w-4 h-3.5 sm:h-4 mr-1.5 sm:mr-2 flex-shrink-0" />
                      <span className="truncate">Period: {formatDate(dataroom.subscription.current_period_starts_at)} - {formatDate(dataroom.subscription.current_period_ends_at)}</span>
                    </div>
                  )}
                  {dataroom.region && (
                    <div className="flex items-center text-xs sm:text-sm text-gray-600">
                      <MapPin className="w-3.5 sm:w-4 h-3.5 sm:h-4 mr-1.5 sm:mr-2 flex-shrink-0" />
                      <span className="truncate">{dataroom.region.name}</span>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between mb-3">
                  <div>
                    <span className="text-xs text-gray-500 block mb-0.5 sm:mb-1">Status</span>
                    {getStatusBadge(dataroom.status, dataroom.status_text)}
                  </div>
                  <div>
                    <span className="text-xs text-gray-500 block mb-0.5 sm:mb-1">Approval</span>
                    {getApprovalBadge(dataroom.approval_status, dataroom.approval_status_text)}
                  </div>
                </div>

                {(dataroom.approval_status === 0 || dataroom.approval_status === 'pending' || dataroom.approval_status_text?.toLowerCase() === 'pending') && (
                  <div className="mb-2 sm:mb-3 p-2 bg-yellow-50 rounded-lg">
                    <p className="text-xs sm:text-sm text-yellow-800 flex items-start">
                      <Info className="w-3.5 sm:w-4 h-3.5 sm:h-4 mr-1.5 sm:mr-2 mt-0.5 flex-shrink-0" />
                      <span>Your dataroom is pending approval. You'll be notified once it's reviewed.</span>
                    </p>
                  </div>
                )}

                {/* Action Buttons - Only for unapproved datarooms */}
                {!(dataroom.approval_status === 1 || dataroom.approval_status === 'approved' || dataroom.approval_status_text?.toLowerCase() === 'approved') && (
                  <div className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-gray-200">
                    <div className="flex justify-center gap-2" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewDetails(dataroom);
                        }}
                        className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 transition-colors flex items-center justify-center text-xs font-medium"
                      >
                        <Eye className="w-3.5 h-3.5 mr-1.5" />
                        View Details
                      </button>

                      {/* Edit button - Only show when is_owner: true, approval_status: 0, status: 0 */}
                      {dataroom.is_owner === true &&
                       (dataroom.approval_status === 0 || dataroom.approval_status === 'pending') &&
                       (dataroom.status === 0 || dataroom.status === 'inactive') && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditDataroom(dataroom);
                          }}
                          className="px-3 py-1.5 bg-green-50 text-green-600 rounded-md hover:bg-green-100 transition-colors flex items-center justify-center text-xs font-medium"
                        >
                          <Edit3 className="w-3.5 h-3.5 mr-1.5" />
                          Edit
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          ))
        )}
      </div>

      {/* Create Modal - 2 Step Wizard */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="sticky top-0 bg-white border-b px-6 py-4">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-semibold">
                    {wizardStep === 1 ? 'Select Subscription Plan' : 'Create New Dataroom'}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Step {wizardStep} of 2
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Step 1: Select Pricing Plan */}
            {wizardStep === 1 && (
              <div className="p-8">
                <div className="text-center mb-8">
                  <h4 className="text-2xl font-bold text-gray-900 mb-2">Choose Your Plan</h4>
                  <p className="text-gray-600 text-base">Select a subscription plan that best fits your needs</p>
                </div>

                {plansLoading ? (
                  <GridLoader cards={3} />
                ) : pricingPlans.length === 0 ? (
                  <div className="text-center py-16">
                    <Package className="mx-auto h-16 w-16 text-gray-300" />
                    <h3 className="mt-4 text-lg font-medium text-gray-900">No pricing plans available</h3>
                    <p className="mt-2 text-gray-500">Please contact support if this issue persists.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    {pricingPlans.map((plan) => (
                    <motion.div
                      key={plan.id}
                      whileHover={{ y: -4 }}
                      whileTap={{ scale: 0.98 }}
                      className={`relative border-2 rounded-xl p-6 cursor-pointer transition-all ${
                        selectedPlan?.id === plan.id
                          ? 'border-blue-500 bg-blue-50 shadow-xl ring-2 ring-blue-200'
                          : 'border-gray-200 bg-white hover:border-blue-300 hover:shadow-lg'
                      }`}
                      onClick={() => handlePlanSelection(plan)}
                    >
                      {selectedPlan?.id === plan.id && (
                        <div className="absolute -top-3 -right-3 bg-blue-500 text-white rounded-full p-1.5 shadow-lg">
                          <CheckCircle className="w-5 h-5" />
                        </div>
                      )}

                      <div className="text-center mb-6">
                        <h5 className="text-xl font-bold text-gray-900 mb-1">{plan.name}</h5>
                        <div className="mt-4">
                          <span className="text-4xl font-extrabold text-gray-900">₹{plan.price}</span>
                          <span className="text-sm font-medium text-gray-500 ml-1">/month</span>
                        </div>
                      </div>

                      <div className="border-t border-gray-200 pt-6">
                        <ul className="space-y-3">
                          <li className="flex items-start text-sm text-gray-700">
                            <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span><span className="font-semibold">{plan.storage} GB</span> Storage</span>
                          </li>
                          <li className="flex items-start text-sm text-gray-700">
                            <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span>Up to <span className="font-semibold">{plan.user_limit}</span> Users</span>
                          </li>
                          {plan.features && plan.features.length > 0 && plan.features.map((feature, index) => (
                            <li key={index} className="flex items-start text-sm text-gray-700">
                              <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                              <span>{typeof feature === 'string' ? feature : feature.name}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </motion.div>
                    ))}
                  </div>
                )}

                <div className="flex justify-end pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={handleNextStep}
                    className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-colors ${
                      selectedPlan
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                    disabled={!selectedPlan}
                  >
                    Next: Dataroom Details
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: Dataroom Form */}
            {wizardStep === 2 && (
              <form onSubmit={handleCreateDataroom} className="p-6">
                {/* Selected Plan Summary */}
                {selectedPlan && (
                  <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <h5 className="font-medium text-blue-900">Selected Plan: {selectedPlan.name}</h5>
                        <p className="text-sm text-blue-700">
                          ₹{selectedPlan.price}/month • {selectedPlan.storage} GB Storage • {selectedPlan.user_limit} Users
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={handlePreviousStep}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                      >
                        Change Plan
                      </button>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Basic Information */}
                <div className="col-span-2">
                  <h4 className="text-lg font-medium mb-4">Basic Information</h4>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Dataroom Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className={`w-full px-3 py-1.5 focus:border-transparent text-sm border rounded-lg focus:outline-none focus:ring-2 border-gray-300 focus:ring-primary-500 ${
                      formErrors.name ? 'border-red-500' : ''
                    }`}
                  />
                  {formErrors.name && <ErrorMessage error={formErrors.name} />}
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Logo
                  </label>
                  {/* Logo Preview */}
                  {logoPreview && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.3 }}
                      className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg border border-gray-200 mb-4"
                    >
                      <div className="relative group">
                        <img
                          src={logoPreview}
                          alt="Logo preview"
                          className="h-16 w-16 rounded-lg object-cover border border-gray-300"
                        />
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ delay: 0.2, type: "spring" }}
                          className="absolute -top-1 -right-1 bg-green-500 text-white rounded-full p-0.5"
                        >
                          <Check className="w-3 h-3" />
                        </motion.div>
                      </div>
                      <div className="flex-1 flex items-center">
                        <motion.button
                          type="button"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => {
                            setLogoPreview(null);
                            setFormData({ ...formData, logo: null });
                            toast('Logo removed', { icon: '🖼️' });
                          }}
                          className="px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          Change logo
                        </motion.button>
                      </div>
                    </motion.div>
                  )}

                  {/* Upload Area and Requirements Side by Side */}
                  {!logoPreview && (
                    <div className="flex gap-4">
                      {/* Upload Area */}
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                        className="relative flex-1"
                        onDragEnter={handleDragEnter}
                        onDragLeave={handleDragLeave}
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                      >
                        <input
                          type="file"
                          accept="image/jpeg,image/jpg,image/png,image/gif"
                          onChange={handleFileChange}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                          id="logo-upload"
                        />
                        <label
                          htmlFor="logo-upload"
                          className={`relative overflow-hidden flex flex-col items-center justify-center w-full h-40 border-2 border-dashed rounded-xl cursor-pointer transition-all duration-300 ${
                            isDragging
                              ? 'border-blue-500 bg-blue-50 shadow-lg scale-[1.02]'
                              : 'border-gray-300 bg-gray-50 hover:border-gray-400 hover:shadow-md'
                          }`}
                        >
                          {/* Background Pattern */}
                          <div className="absolute inset-0 opacity-5">
                            <div className="absolute inset-0" style={{
                              backgroundImage: 'url("data:image/svg+xml,%3Csvg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="%239CA3AF" fill-opacity="0.4"%3E%3Cpath d="M0 40L40 0H20L0 20M40 40V20L20 40"/%3E%3C/g%3E%3C/svg%3E")'
                            }}></div>
                          </div>

                          <div className="relative flex flex-col items-center justify-center pt-5 pb-6">
                            <motion.div
                              animate={isDragging ? { scale: 1.1, rotate: 5 } : { scale: 1, rotate: 0 }}
                              transition={{ type: "spring", stiffness: 300 }}
                            >
                              <Upload className={`w-10 h-10 mb-3 ${isDragging ? 'text-blue-500' : 'text-gray-400'}`} />
                            </motion.div>
                            <p className="mb-2 text-sm text-gray-700">
                              {isDragging ? (
                                <span className="font-semibold text-blue-600 text-base">Drop your logo here</span>
                              ) : (
                                <>
                                  <span className="font-semibold">Click to upload</span>
                                  <span className="text-gray-500"> or drag and drop</span>
                                </>
                              )}
                            </p>
                            <p className="text-xs text-gray-500">PNG, JPG or GIF (MAX. 2MB)</p>
                            <div className="mt-2 flex items-center gap-2">
                              <span className="px-2 py-1 text-xs bg-white rounded-md border border-gray-200">PNG</span>
                              <span className="px-2 py-1 text-xs bg-white rounded-md border border-gray-200">JPG</span>
                              <span className="px-2 py-1 text-xs bg-white rounded-md border border-gray-200">GIF</span>
                            </div>
                          </div>
                        </label>
                      </motion.div>

                      {/* Logo Requirements - Right Side */}
                      <div className="flex items-center">
                        <div className="text-[11px] text-gray-500 space-y-1">
                          <p className="font-semibold text-gray-600">Logo requirements:</p>
                          <p>• Min: 200×200px</p>
                          <p>• Max: 2MB</p>
                          <p>• PNG, JPG, GIF</p>
                          <p>• Use PNG for transparency</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    className={`w-full px-3 py-1.5 focus:border-transparent text-sm border rounded-lg focus:outline-none focus:ring-2 border-gray-300 focus:ring-primary-500 ${
                      formErrors.company_name ? 'border-red-500' : ''
                    }`}
                  />
                  {formErrors.company_name && <ErrorMessage error={formErrors.company_name} />}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Brand Name
                  </label>
                  <input
                    type="text"
                    value={formData.brand_name}
                    onChange={(e) => setFormData({ ...formData, brand_name: e.target.value })}
                    className="w-full px-3 py-1.5 focus:border-transparent text-sm border rounded-lg focus:outline-none focus:ring-2 border-gray-300 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Organization Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.organization_type}
                    onChange={(e) => setFormData({ ...formData, organization_type: e.target.value })}
                    className={`w-full px-3 py-1.5 focus:border-transparent text-sm border rounded-lg focus:outline-none focus:ring-2 border-gray-300 focus:ring-primary-500 ${
                      formErrors.organization_type ? 'border-red-500' : ''
                    }`}
                  >
                    <option value="">Select Organization Type</option>
                    <option value="Private Company">Private Company</option>
                    <option value="Public Company">Public Company</option>
                    <option value="LLP">LLP</option>
                    <option value="Partnership Firm">Partnership Firm</option>
                    <option value="Sole Proprietorship">Sole Proprietorship</option>
                    <option value="Others">Others</option>
                  </select>
                  {formErrors.organization_type && <ErrorMessage error={formErrors.organization_type} />}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Industry <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.industry}
                    onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                    className={`w-full px-3 py-1.5 focus:border-transparent text-sm border rounded-lg focus:outline-none focus:ring-2 border-gray-300 focus:ring-primary-500 ${
                      formErrors.industry ? 'border-red-500' : ''
                    }`}
                  />
                  {formErrors.industry && <ErrorMessage error={formErrors.industry} />}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Transaction Type
                  </label>
                  <select
                    value={formData.transaction_type}
                    onChange={(e) => setFormData({ ...formData, transaction_type: e.target.value })}
                    className="w-full px-3 py-1.5 focus:border-transparent text-sm border rounded-lg focus:outline-none focus:ring-2 border-gray-300 focus:ring-primary-500"
                  >
                    <option value="">Select type</option>
                    <option value="M&A">M&A</option>
                    <option value="IPO">IPO</option>
                    <option value="Due Diligence">Due Diligence</option>
                    <option value="Fundraising">Fundraising</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Registered Office Address <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={formData.registered_office_address}
                    onChange={(e) => setFormData({ ...formData, registered_office_address: e.target.value })}
                    rows={2}
                    className={`w-full px-3 py-1.5 focus:border-transparent text-sm border rounded-lg focus:outline-none focus:ring-2 border-gray-300 focus:ring-primary-500 ${
                      formErrors.registered_office_address ? 'border-red-500' : ''
                    }`}
                  />
                  {formErrors.registered_office_address && <ErrorMessage error={formErrors.registered_office_address} />}
                </div>

                {/* Compliance Details */}
                <div className="col-span-2 mt-4">
                  <h4 className="text-lg font-medium mb-4">Compliance Details</h4>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Corporate Identification Number (CIN) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.corporate_identification_no}
                    onChange={(e) => setFormData({ ...formData, corporate_identification_no: e.target.value })}
                    className={`w-full px-3 py-1.5 focus:border-transparent text-sm border rounded-lg focus:outline-none focus:ring-2 border-gray-300 focus:ring-primary-500 ${
                      formErrors.corporate_identification_no ? 'border-red-500' : ''
                    }`}
                  />
                  {formErrors.corporate_identification_no && <ErrorMessage error={formErrors.corporate_identification_no} />}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    PAN Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.pan_number}
                    onChange={(e) => setFormData({ ...formData, pan_number: e.target.value })}
                    className={`w-full px-3 py-1.5 focus:border-transparent text-sm border rounded-lg focus:outline-none focus:ring-2 border-gray-300 focus:ring-primary-500 ${
                      formErrors.pan_number ? 'border-red-500' : ''
                    }`}
                  />
                  {formErrors.pan_number && <ErrorMessage error={formErrors.pan_number} />}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    TAN Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.tan_number}
                    onChange={(e) => setFormData({ ...formData, tan_number: e.target.value })}
                    className={`w-full px-3 py-1.5 focus:border-transparent text-sm border rounded-lg focus:outline-none focus:ring-2 border-gray-300 focus:ring-primary-500 ${
                      formErrors.tan_number ? 'border-red-500' : ''
                    }`}
                  />
                  {formErrors.tan_number && <ErrorMessage error={formErrors.tan_number} />}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    GSTIN <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.gstin}
                    onChange={(e) => setFormData({ ...formData, gstin: e.target.value })}
                    className={`w-full px-3 py-1.5 focus:border-transparent text-sm border rounded-lg focus:outline-none focus:ring-2 border-gray-300 focus:ring-primary-500 ${
                      formErrors.gstin ? 'border-red-500' : ''
                    }`}
                  />
                  {formErrors.gstin && <ErrorMessage error={formErrors.gstin} />}
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Organization Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={formData.organization_email}
                    onChange={(e) => setFormData({ ...formData, organization_email: e.target.value.toLowerCase() })}
                    placeholder="organization@example.com"
                    className={`w-full px-3 py-1.5 focus:border-transparent text-sm border rounded-lg focus:outline-none focus:ring-2 border-gray-300 focus:ring-primary-500 ${
                      formErrors.organization_email ? 'border-red-500' : ''
                    }`}
                  />
                  {formErrors.organization_email && <ErrorMessage error={formErrors.organization_email} />}
                </div>

                {/* Contact Information */}
                <div className="col-span-2 mt-4">
                  <h4 className="text-lg font-medium mb-4">Contact Information</h4>
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Website <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="url"
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    placeholder="https://example.com"
                    className={`w-full px-3 py-1.5 focus:border-transparent text-sm border rounded-lg focus:outline-none focus:ring-2 border-gray-300 focus:ring-primary-500 ${
                      formErrors.website ? 'border-red-500' : ''
                    }`}
                  />
                  {formErrors.website && <ErrorMessage error={formErrors.website} />}
                </div>

                {/* Primary Contact Person Section */}
                <div className="col-span-2 mt-2">
                  <h5 className="text-md font-medium text-gray-800">Primary Contact Person</h5>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.primary_contact_name}
                    onChange={(e) => setFormData({ ...formData, primary_contact_name: e.target.value })}
                    className={`w-full px-3 py-1.5 focus:border-transparent text-sm border rounded-lg focus:outline-none focus:ring-2 border-gray-300 focus:ring-primary-500 ${
                      formErrors.primary_contact_name ? 'border-red-500' : ''
                    }`}
                  />
                  {formErrors.primary_contact_name && <ErrorMessage error={formErrors.primary_contact_name} />}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contact Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    value={formData.primary_contact_no}
                    onChange={(e) => setFormData({ ...formData, primary_contact_no: e.target.value })}
                    placeholder="+91 XXXXXXXXXX"
                    className={`w-full px-3 py-1.5 focus:border-transparent text-sm border rounded-lg focus:outline-none focus:ring-2 border-gray-300 focus:ring-primary-500 ${
                      formErrors.primary_contact_no ? 'border-red-500' : ''
                    }`}
                  />
                  {formErrors.primary_contact_no && <ErrorMessage error={formErrors.primary_contact_no} />}
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email ID <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={formData.primary_contact_email}
                    onChange={(e) => setFormData({ ...formData, primary_contact_email: e.target.value.toLowerCase() })}
                    placeholder="primary@example.com"
                    className={`w-full px-3 py-1.5 focus:border-transparent text-sm border rounded-lg focus:outline-none focus:ring-2 border-gray-300 focus:ring-primary-500 ${
                      formErrors.primary_contact_email ? 'border-red-500' : ''
                    }`}
                  />
                  {formErrors.primary_contact_email && <ErrorMessage error={formErrors.primary_contact_email} />}
                </div>

                {/* Secondary Contact Person Section */}
                <div className="col-span-2 mt-2">
                  <h5 className="text-md font-medium text-gray-800">Secondary Contact Person</h5>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.secondary_contact_name}
                    onChange={(e) => setFormData({ ...formData, secondary_contact_name: e.target.value })}
                    className={`w-full px-3 py-1.5 focus:border-transparent text-sm border rounded-lg focus:outline-none focus:ring-2 border-gray-300 focus:ring-primary-500 ${
                      formErrors.secondary_contact_name ? 'border-red-500' : ''
                    }`}
                  />
                  {formErrors.secondary_contact_name && <ErrorMessage error={formErrors.secondary_contact_name} />}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contact Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    value={formData.secondary_contact_no}
                    onChange={(e) => setFormData({ ...formData, secondary_contact_no: e.target.value })}
                    placeholder="+91 XXXXXXXXXX"
                    className={`w-full px-3 py-1.5 focus:border-transparent text-sm border rounded-lg focus:outline-none focus:ring-2 border-gray-300 focus:ring-primary-500 ${
                      formErrors.secondary_contact_no ? 'border-red-500' : ''
                    }`}
                  />
                  {formErrors.secondary_contact_no && <ErrorMessage error={formErrors.secondary_contact_no} />}
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email ID <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={formData.secondary_contact_email}
                    onChange={(e) => setFormData({ ...formData, secondary_contact_email: e.target.value.toLowerCase() })}
                    placeholder="secondary@example.com"
                    className={`w-full px-3 py-1.5 focus:border-transparent text-sm border rounded-lg focus:outline-none focus:ring-2 border-gray-300 focus:ring-primary-500 ${
                      formErrors.secondary_contact_email ? 'border-red-500' : ''
                    }`}
                  />
                  {formErrors.secondary_contact_email && <ErrorMessage error={formErrors.secondary_contact_email} />}
                </div>

              </div>

              <div className="mt-6 flex justify-between">
                <CancelButton
                  type="button"
                  onClick={handlePreviousStep}
                  icon={ArrowLeft}
                >
                  Back
                </CancelButton>
                <div className="flex space-x-3">
                  <CancelButton
                    type="button"
                    onClick={() => {
                      setShowCreateModal(false);
                      resetForm();
                    }}
                  >
                    Cancel
                  </CancelButton>
                  <CreateButton
                    type="submit"
                  >
                    Create Dataroom
                  </CreateButton>
                </div>
              </div>
              </form>
            )}
          </motion.div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedDataroom && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="sticky top-0 bg-white border-b px-6 py-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">Dataroom Details</h3>
                <button
                  onClick={() => {
                    setShowDetailsModal(false);
                    setSelectedDataroom(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6">
              {/* Logo and Basic Info */}
              <div className="flex items-start mb-6">
                
                {selectedDataroom.logo_url ? (
                  <img
                    className="h-20 w-20 rounded-lg object-cover"
                    src={selectedDataroom.logo_url}
                    alt={selectedDataroom.name}
                  />
                ) : (
                  <div className="h-20 w-20 rounded-lg bg-gray-300 flex items-center justify-center">
                    <FolderOpen className="h-10 w-10 text-gray-600" />
                  </div>
                )}
                <div className="ml-4">
                  <h4 className="text-xl font-semibold">{selectedDataroom.name}</h4>
                  <p className="text-gray-600">{selectedDataroom.company_name}</p>
                  <div className="mt-2 flex space-x-2">
                    {getStatusBadge(selectedDataroom.status, selectedDataroom.status_text)}
                    {getApprovalBadge(selectedDataroom.approval_status, selectedDataroom.approval_status_text)}
                  </div>
                </div>
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h5 className="font-medium text-gray-900 mb-3 flex items-center">
                    <Building className="w-5 h-5 mr-2" />
                    Organization Details
                  </h5>
                  <dl className="space-y-2">
                    <div>
                      <dt className="text-sm text-gray-500">Brand Name</dt>
                      <dd className="text-sm font-medium">{selectedDataroom.brand_name || 'N/A'}</dd>
                    </div>
                    <div>
                      <dt className="text-sm text-gray-500">Organization Type</dt>
                      <dd className="text-sm font-medium">{selectedDataroom.organization_type || 'N/A'}</dd>
                    </div>
                    <div>
                      <dt className="text-sm text-gray-500">Industry</dt>
                      <dd className="text-sm font-medium">{selectedDataroom.industry || 'N/A'}</dd>
                    </div>
                    <div>
                      <dt className="text-sm text-gray-500">Transaction Type</dt>
                      <dd className="text-sm font-medium">{selectedDataroom.transaction_type || 'N/A'}</dd>
                    </div>
                  </dl>
                </div>

                {/* Subscription Details - Show for all users */}
                <div>
                  <h5 className="font-medium text-gray-900 mb-3 flex items-center">
                    <Package className="w-5 h-5 mr-2" />
                    Subscription Details
                  </h5>
                  <dl className="space-y-2">
                    <div>
                      <dt className="text-sm text-gray-500">Pricing Plan</dt>
                      <dd className="text-sm font-medium">{selectedDataroom.pricing_plan?.name || 'N/A'}</dd>
                    </div>
                    <div>
                      <dt className="text-sm text-gray-500">Price</dt>
                      <dd className="text-sm font-medium">
                        {selectedDataroom.pricing_plan ? `₹${selectedDataroom.pricing_plan.price}/mo` : 'N/A'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm text-gray-500">Storage</dt>
                      <dd className="text-sm font-medium">
                        {selectedDataroom.pricing_plan ? `${selectedDataroom.pricing_plan.storage} GB` : 'N/A'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm text-gray-500">User Limit</dt>
                      <dd className="text-sm font-medium">
                        {selectedDataroom.pricing_plan?.user_limit || 'N/A'}
                      </dd>
                    </div>
                  </dl>
                </div>
              </div>

              {/* Timestamps */}
              <div className="mt-6 pt-6 border-t">
                <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <dt className="text-sm text-gray-500">Created At</dt>
                    <dd className="text-sm font-medium">{formatDate(selectedDataroom.created_at)}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-gray-500">Updated At</dt>
                    <dd className="text-sm font-medium">{formatDate(selectedDataroom.updated_at)}</dd>
                  </div>
                </dl>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedDataroom && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
          >
            <div className="flex items-center mb-4">
              <AlertTriangle className="h-6 w-6 text-red-600 mr-2" />
              <h3 className="text-lg font-semibold">Delete Dataroom</h3>
            </div>
            <p className="text-gray-600 mb-4">
              Are you sure you want to delete "{selectedDataroom.name}"? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <CancelButton
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedDataroom(null);
                }}
              >
                Cancel
              </CancelButton>
              <DeleteButton
                onClick={handleDeleteDataroom}
              >
                Delete
              </DeleteButton>
            </div>
          </motion.div>
        </div>
      )}

      {/* Edit Dataroom Modal */}
      {showEditModal && selectedDataroom && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-semibold">Edit Dataroom</h3>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedDataroom(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              <EditDataroomForm
                dataroom={selectedDataroom}
                onSave={handleUpdateDataroom}
                onCancel={() => {
                  setShowEditModal(false);
                  setSelectedDataroom(null);
                }}
              />
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default DataroomList;